import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { apiGet } from "@/lib/api";
import { RichText } from "@/components/content/RichText";
import type { PageDto } from "@/types";

interface ServiceLink {
  id: string;
  slot: string;
  label: string;
  href: string;
  icon?: string;
  position: number;
  children: Array<{ title: string; path: string; slug: string }>;
}

/**
 * "Nuetzliche Services" - landing page that takes over what used to be
 * the Schnellzugriff sidebar widget (specification 4).
 *
 * Pulls two data sources:
 *   1. the `/services` Page (managed in pages table) for header / intro,
 *   2. `/public/navigation/slots?slot=services` for the link tiles.
 *
 * Both are cached and fully editable from the admin panel.
 */
export function ServicesPage() {
  const { data: page } = useQuery<PageDto>({
    queryKey: ["services-page"],
    queryFn: () => apiGet<PageDto>("/public/pages/services?locale=de"),
    staleTime: 5 * 60_000,
  });

  const { data: items = [] } = useQuery<ServiceLink[]>({
    queryKey: ["services-links"],
    queryFn: () =>
      apiGet<ServiceLink[]>("/public/navigation/slots?slot=services&locale=de"),
    staleTime: 5 * 60_000,
  });

  return (
    <>
      <Helmet>
        <title>{(page?.title ?? "Nuetzliche Services") + " - Falkenrehde"}</title>
        <meta
          name="description"
          content="Nuetzliche Services, Formulare und externe Anlaufstellen fuer das Leben in Falkenrehde."
        />
      </Helmet>

      <article>
        <header className="mb-8">
          <h1 className="font-serif text-4xl lg:text-5xl font-bold text-primary">
            {page?.title ?? "Nuetzliche Services"}
          </h1>
          {page?.bodyHtml && (
            <div className="mt-4">
              <RichText html={page.bodyHtml} />
            </div>
          )}
        </header>

        {items.length > 0 ? (
          <section aria-label="Service-Liste">
            <h2 className="font-serif text-2xl text-primary mb-4">Direktlinks</h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((it) => (
                <li key={it.id}>
                  <ServiceTile item={it} />
                </li>
              ))}
            </ul>
          </section>
        ) : (
          <p className="text-text-muted italic">
            Noch keine Services hinterlegt. Inhalte koennen ueber das Admin-Panel
            ergaenzt werden.
          </p>
        )}
      </article>
    </>
  );
}

function ServiceTile({ item }: { item: ServiceLink }) {
  const external = /^https?:\/\//i.test(item.href);
  const Tag = external ? "a" : "a"; // both render <a>; internal href still works
  return (
    <Tag
      href={item.href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className="block p-5 rounded-lg border border-border bg-bg card-hover group"
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-primary-soft text-primary shrink-0">
          <ServiceIcon name={item.icon} />
        </span>
        <div className="min-w-0">
          <div className="font-semibold text-text group-hover:text-primary transition-colors">
            {item.label}
          </div>
          <div className="text-xs text-text-muted truncate mt-0.5">
            {prettyHref(item.href)}
          </div>
        </div>
      </div>
    </Tag>
  );
}

function prettyHref(href: string): string {
  if (href.startsWith("tel:")) return "Telefon: " + href.slice(4);
  if (href.startsWith("mailto:")) return href.slice(7);
  try {
    if (href.startsWith("http")) return new URL(href).hostname;
  } catch { /* fall through */ }
  return href;
}

function ServiceIcon({ name }: { name?: string }) {
  // Tiny inline icon set; new icons can be added without a dep.
  const sp = {
    fill: "none" as const, stroke: "currentColor", strokeWidth: 1.7,
    strokeLinecap: "round" as const, strokeLinejoin: "round" as const,
    width: 22, height: 22, viewBox: "0 0 24 24",
  };
  switch (name) {
    case "phone":
      return (<svg {...sp}><path d="M5 4h4l2 5-3 2a14 14 0 0 0 6 6l2-3 5 2v4a2 2 0 0 1-2 2A18 18 0 0 1 3 6a2 2 0 0 1 2-2Z"/></svg>);
    case "mail":
      return (<svg {...sp}><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>);
    case "calendar":
      return (<svg {...sp}><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M16 3v4M8 3v4M3 10h18"/></svg>);
    case "info":
      return (<svg {...sp}><circle cx="12" cy="12" r="9"/><path d="M12 8v.1M11 12h1v5h1"/></svg>);
    default:
      return (<svg {...sp}><path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1"/><path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1"/></svg>);
  }
}
